import { ChangeEnum, CountingDifference, CountingItem, CountingOrder, CountingOrderAdd, CountingOrderAddItem, CountingStateEnum, His, Inventory, Order, OrderAdd, OrderAddItem, OperateEnum } from "@/types/inventory";
import { isArray, isNumber, isString } from "lodash";
import request from ".";
import { CommonListParams, SourceTypeEnum } from "@/types/common";
import { ScanOrder } from "@/types/scan";
import { isEnum, isStringEmpty, isScan, isCountingItem, isCountingOrder, isInventory, isInventoryHis, isOrder } from "@/utils/checkTool";
import { cleanToCountingItem, cleanToCountingOrder, cleanToHis, cleanToInventory, cleanToOrder, cleanToScan } from "@/utils/cleanData";
import { getStorage } from "@/utils/storage";
import { StoreTypeEnum } from "@/types/product";

const url = '/wms/inventory';

const apis = {
  list: (categoryId?: string) => url + `${categoryId ? '/' + categoryId : ''}/list`,
  orderAdd: (categoryId?: string) => url + `${categoryId ? '/' + categoryId : ''}/order/add`,
  orderList: ({ categoryId, changeType }: {changeType?: ChangeEnum, categoryId?: string}) => url + `${categoryId ? '/' + categoryId : ''}/order${changeType ? '/' + changeType : ''}/list`,
  countingAdd: (categoryId?: string) => url + `${categoryId ? '/' + categoryId : ''}/counting/add`,
  countingList: (categoryId?: string) => url + `${categoryId ? '/' + categoryId : ''}/counting/list`,
  orderFetch: (id: string) => url + `/order/${id}/fetch`,
  orderDisable: (id: string) => url + `/order/${id}/disable`,
  orderConfirm: (id: string) => url + `/order/${id}/confirm`,
  hisInventory: (id: string) => url + `/${id}/his`,
  inventoryBarcodes: (id: string) => url + `/${id}/barcodes`,
  countingFetch: (id: string) => url + `/counting/${id}/fetch`,
  countingDisable: (id: string) => url + `/counting/${id}/disable`,
  countingItemAdd: (id: string) => url + `/counting/${id}/item/add`,
  countingScanAdd: (id: string) => url + `/counting/${id}/scan/add`,
  countingItemsRm: (id: string) => url + `/counting/${id}/items/rm`,
  countingScanRm: (id: string) => url + `/counting/${id}/scan/rm`,
  countingRecount: (id: string) => url + `/counting/${id}/recount`,
  countingDo: (id: string) => url + `/counting/${id}/do`,
  countingOrderStateUpdate: (id: string) => url + `/counting/${id}/orderState/update`,
  delOrderCodes: url + `/order/item/barcode/rms`,
  scansAdd: (id: string) => url + `/order/${id}/scans/add`,
  updateOrderQuantity: (id: string) => url + `/order/item/${id}/quantity/update`,
}

interface OrderAddProps {
  orgId: string;
  rootCategoryId?: string;
  order: OrderAdd;
}
export const orderAdd = ({ order, orgId, rootCategoryId }: OrderAddProps) => {
  if (!isEnum(order.changeType, ChangeEnum) || !isEnum(order.opType, OperateEnum) || !isEnum(order.sourceType, SourceTypeEnum) || 
      !isString(order.sourceId) || !isArray<OrderAddItem>(order.items) || !isArray<string>(order.scans) || !isString(orgId) || isStringEmpty(orgId)) {
      return Promise.reject(10012)
  }
  return request<Order, OrderAdd>(apis.orderAdd(rootCategoryId), { params: { body: order }, requestConfig: {
      headers: { org_id: orgId }
  } })
      .then(({ body: res }) => {
          if (isOrder(res)) {
              return Promise.resolve(cleanToOrder(res))
          } else {
              return Promise.reject(10013)
          }
      })
      .catch(err => Promise.reject(err))
}

export const orderFetch = (id: string) => {
  if (id && !isString(id) && isStringEmpty(id)) {
      return Promise.reject(10012)
  }
  return request<Order, string>(apis.orderFetch(id))
      .then(({ body: res }) => {
          if (isOrder(res)) {
              return Promise.resolve(cleanToOrder(res))
          } else {
              return Promise.reject(10013)
          }
      })
      .catch(err => Promise.reject(err))
}

export const orderDisable = (id: string) => {
  return new Promise<void>((resolve, reject) => {
    if (id && !isString(id) && isStringEmpty(id)) {
      return reject(10012)
    }
    request<void, void>(apis.orderDisable(id))
      .then(() => resolve())
      .catch((err) => reject(err));
  });
};

export const orderConfirm = (id: string) => {
  if (id && !isString(id) && isStringEmpty(id)) {
      return Promise.reject(10012)
  }
  return request<void>(apis.orderConfirm(id)).then((res)=>{
    console.log('respromise')
    return Promise.resolve(res)
  }).catch(err=>{
    console.log('respromisereject')
    return Promise.reject(err)
  })
}

export interface OrderListProps {
  orgId: string;
  params?: CommonListParams;
  changeType?: ChangeEnum;
  categoryId?: string;
}

export const orderList = ({orgId, params, changeType, categoryId}: OrderListProps) => {
  if (!isString(orgId) && isStringEmpty(orgId)) {
      return Promise.reject(10012)
  }
  if (params && params.body && !isString(params.body)) {
      return Promise.reject(10012)
  }
  return request<CommonListParams<Order>, string>(apis.orderList({changeType, categoryId}), {
      params, requestConfig: {
          headers: { org_id: orgId }
      }
  })
      .then(res => {
          if (isArray<Order>(res.body)) {
              return Promise.resolve({
                  body: res.body.filter(order => isOrder(order))?.map(order => cleanToOrder(order)),
                  pagination: res.pagination
              })
          } else {
              return Promise.reject(10013)
          }
      })
      .catch(err => Promise.reject(err))
}

export const orderListUnconfirmed = ({orgId, params, changeType, categoryId}: OrderListProps) => {
    if (!isString(orgId) && isStringEmpty(orgId)) {
        return Promise.reject(10012)
    }
    if (params && params.body && !isString(params.body)) {
        return Promise.reject(10012)
    }
    return request<CommonListParams<Order>, string>(apis.orderList({changeType, categoryId}), {
        params, requestConfig: {
            headers: { org_id: orgId }
        }
    })
        .then(res => {
            if (isArray<Order>(res.body)) {
                return Promise.resolve({
                    body: res.body.filter(order => isOrder(order) && order.confirmed === 0)?.map(order => cleanToOrder(order)),
                    pagination: res.pagination
                })
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
  }

export interface InventoryProps {
orgId: string;
categoryId?: string;
params?: CommonListParams;
}

export const inventoryList = ({ orgId, categoryId, params }: InventoryProps) => {
  if (params && params.body && !isString(params.body)) {
      return Promise.reject(10012)
  }
  return request<Array<Inventory>, string>(apis.list(categoryId), { params, requestConfig: { headers: { org_id: orgId || getStorage('curOrgId') } } })
      .then(res => {
          if (isArray<Inventory>(res.body)) {
              return Promise.resolve({
                  body: res.body.filter(inventory => isInventory(inventory))?.map(inventory => cleanToInventory(inventory)),
                  pagination: res.pagination
              })
          } else {
              return Promise.reject(10013)
          }
      })
      .catch(err => Promise.reject(err))
}

export const inventoryHis = (id: string) => {
  if (id && !isString(id) && isStringEmpty(id)) {
      return Promise.reject(10012)
  }
  return request<His, string>(apis.hisInventory(id))
      .then(({ body: res }) => {
          if (isInventoryHis(res)) {
              return Promise.resolve(cleanToHis(res))
          } else {
              return Promise.reject(10013)
          }
      })
      .catch(err => Promise.reject(err))
}

export const inventoryBarcodes = (id: string) => {
  if (id && !isString(id) && isStringEmpty(id)) {
      return Promise.reject(10012)
  }
  return request<Array<string>, string>(apis.inventoryBarcodes(id))
      .then(({ body: res }) => {
          if (isArray<string>(res)) {
              return Promise.resolve(res)
          } else {
              return Promise.reject(10013)
          }
      })
      .catch(err => Promise.reject(err))
}

export const countingAdd = (orgId: string, countingOrder: CountingOrderAdd, categoryId: string) => {
  if (!isArray<OrderAddItem>(countingOrder.items) || !isArray<string>(countingOrder.scans)) {
      return Promise.reject(10012)
  }
  return request<CountingOrder, CountingOrderAdd>(apis.countingAdd(categoryId), { params: { body: countingOrder }, requestConfig: {
      headers: { org_id: orgId }
  }  })
      .then(({ body: res }) => {
          if (isCountingOrder(res)) {
              return Promise.resolve(cleanToCountingOrder(res))
          } else {
              return Promise.reject(10013)
          }
      })
      .catch(err => Promise.reject(err))
}

export const countingFetch = (id: string) => {
  if (id && !isString(id) && isStringEmpty(id)) {
      return Promise.reject(10012)
  }
  return request<CountingOrder, string>(apis.countingFetch(id))
      .then(({ body: res }) => {
          if (isCountingOrder(res)) {
              return Promise.resolve(cleanToCountingOrder(res))
          } else {
              return Promise.reject(10013)
          }
      })
      .catch(err => Promise.reject(err))
}

export const countingDisable = (id: string) => {
  if (id && !isString(id) && isStringEmpty(id)) {
      return Promise.reject(10012)
  }
  return request<void>(apis.countingDisable(id))
}

export const countingItemAdd = (id: string, item: CountingOrderAddItem) => {
  if ((id && !isString(id) && isStringEmpty(id)) || !isString(item.productId) || isStringEmpty(item.productId) || !isNumber(item.counting)) {
      return Promise.reject(10012)
  }
  return request<Array<CountingItem>, Array<CountingOrderAddItem>>(apis.countingItemAdd(id), { params: { body: [item] } })
      .then(res => {
          if (isArray<CountingItem>(res.body)) {
              return Promise.resolve({
                  body: res.body.filter(item => isCountingItem(item))?.map(item => cleanToCountingItem(item)),
                  pagination: res.pagination
              })
          } else {
              return Promise.reject(10013)
          }
      })
      .catch(err => Promise.reject(err))
}

export const countingScanAdd = (id: string, scan: string) => {
  if ((id && !isString(id) && isStringEmpty(id)) || (id && !isString(scan) && isStringEmpty(scan))) {
      return Promise.reject(10012)
  }
  return request<Array<ScanOrder>, Array<string>>(apis.countingScanAdd(id), { params: { body: [scan] } })
      .then(res => {
          if (isArray<ScanOrder>(res.body)) {
              return Promise.resolve({
                  body: res.body.filter(scan => isScan(scan))?.map(scan => cleanToScan(scan)),
                  pagination: res.pagination
              })
          } else {
              return Promise.reject(10013)
          }
      })
      .catch(err => Promise.reject(err))
}

export const countingItemsRm = (id: string, rm: Array<string>) => {
  if ((!isString(id) && isStringEmpty(id)) || !isArray<string>(rm)) {
      return Promise.reject(10012)
  }
  return request<void, Array<string>>(apis.countingItemsRm(id))
}

export const countingScanRm = (id: string, rm: Array<string>) => {
  if ((isString(id) && isStringEmpty(id)) || !isArray<string>(rm)) {
      return Promise.reject(10012)
  }
  return request<void, Array<string>>(apis.countingScanRm(id))
}

export const countingRecount = (id: string, count: number) => {
  if ((!isString(id) && isStringEmpty(id)) || (!isNumber(count) && count === null)) {
      return Promise.reject(10012)
  }
  return request<CountingItem, number>(apis.countingRecount(id))
      .then(({ body: res }) => {
          if (res) {
              return Promise.resolve(res)
          } else {
              return Promise.reject(10013)
          }
      })
      .catch(err => Promise.reject(err))
}

export const countingList = (orgId: string, categoryId: string, params?: CommonListParams) => {
  if (params && params.body && !isString(params.body)) {
      return Promise.reject(10012)
  }
  return request<Array<CountingOrder>, string>(apis.countingList(categoryId), { params, requestConfig: {
      headers: { org_id: orgId }
  }  })
      .then(res => {
          if (isArray<CountingOrder>(res.body)) {
              return Promise.resolve({
                  body: res.body.filter(counting => isCountingOrder(counting))?.map(counting => cleanToCountingOrder(counting)),
                  pagination: res.pagination
              })
          } else {
              return Promise.reject(10013)
          }
      })
      .catch(err => Promise.reject(err))
}

export const countingDo = (id: string) => {
  if (!isString(id) && isStringEmpty(id)) {
      return Promise.reject(10012)
  }
  return request<CountingDifference, string>(apis.countingDo(id))
      .then(({ body: res }) => {
        return Promise.resolve(res)
      })
      .catch(err => Promise.reject(err))
}

export const countingOrderStateUpdate = (id: string, state: CountingStateEnum) => {
  if ((!isString(id) && isStringEmpty(id)) || !isNumber(state)) {
      return Promise.reject(10012)
  }
  return request<void, CountingStateEnum>(apis.countingOrderStateUpdate(id), {params: { body: state}})
}

export const delOrderCodes = (ids: Array<string>) => {
  if (!isArray(ids)) {
      return Promise.reject(10012)
  }
  return request<void, Array<string>>(apis.delOrderCodes, {params: { body: ids}})
}

export const scansAdd = (id: string, ids: Array<string>) => {
    if (!isArray(ids)) {
        return Promise.reject(10012)
    }
    return request<Order, Array<string>>(apis.scansAdd(id), {params: { body: ids}}).then(({body: res})=>{
      return res
    })
  }

  export const updateOrderQuantity = (id: string, quantity: number) => {
    if (!id || !quantity) {
        return Promise.reject(10012)
    }
    return request<CountingOrder, number>(apis.updateOrderQuantity(id), { params: { body: quantity } })
        .then(({ body: res }) => {
            if (res) {
                return Promise.resolve(res)
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
  }

// ===== v2 API =====
const v2Url = '/wms/v2/inventory';

const v2Apis = {
  orderList: (type?: number) => v2Url + `/order${type !== undefined ? `/${type}` : ''}/list`,
  orderFetch: (id: string) => v2Url + `/order/${id}/fetch`,
  orderItemUpdate: (id: string) => v2Url + `/order/item/${id}/update`,
};

export interface V2OrderListParams {
  orgId: string;
  type?: number;  // 订单类型，对应 changeType
  keyword?: string;
  params?: {
    body?: string;
    ints?: number[];
    pagination?: {
      offset: number;
      take: number;
    };
  };
}

export interface V2InventoryOrder {
  id: string;
  state: number;
  createdAt: number;
  createdBy: string;
  updatedAt: number;
  updatedBy: string;
  creator?: any;
  updater?: any;
  number: string;
  type: number;
  opType: number;
  opType2?: number;
  desc?: string;
  logBookId: string;
  confirmState: number;
  confirmedAt?: number;
  orderTime?: number;
  preTime?: number;
  arrivalAt?: number;
  returnOrderId?: string;
  items?: any[];
}

// V2 订单列表查询
export const v2OrderList = ({ orgId, type, keyword, params }: V2OrderListParams) => {
  if (!orgId) {
    return Promise.reject(10012);
  }

  const requestParams: any = {
    requestConfig: {
      headers: { org_id: orgId }
    }
  };

  if (params) {
    requestParams.params = params;
  }

  return request<CommonListParams<V2InventoryOrder>, string>(v2Apis.orderList(type), requestParams)
    .then(res => {
      if (isArray<V2InventoryOrder>(res.body)) {
        // 将 V2InventoryOrder 映射为 Order
        const orders: Order[] = res.body.map(v2Order => ({
          id: v2Order.id,
          orgId: orgId,
          number: v2Order.number,
          changeType: v2Order.type as unknown as ChangeEnum,  // type 字段对应 changeType
          opType: v2Order.opType as unknown as OperateEnum,
          sourceType: 1,  // 默认值
          sourceId: v2Order.logBookId,
          confirmed: v2Order.confirmState,
          createdAt: v2Order.createdAt,
          updatedAt: v2Order.updatedAt,
          creator: v2Order.creator,
          orderType: v2Order.type,  // 订单类型
          arrivalAt: v2Order.arrivalAt,
          preTime: v2Order.preTime,
          items: v2Order.items || [],
          state: v2Order.state,
          source: '',  // 添加缺失字段
          scans: [],  // 添加缺失字段
          returnOrderId: v2Order.returnOrderId,  // 添加缺失字段
        } as unknown as Order));

        return Promise.resolve({
          body: orders,
          pagination: res.pagination
        });
      } else {
        return Promise.reject(10013);
      }
    })
    .catch(err => Promise.reject(err));
}

// V2 订单详情获取
export const v2OrderFetch = (id: string) => {
  if (!id || (id && !isString(id) && isStringEmpty(id))) {
    return Promise.reject(10012);
  }

  return request<V2InventoryOrder, string>(v2Apis.orderFetch(id))
    .then(({ body: res }) => {
      // 直接返回 V2InventoryOrder，调用者需要处理类型转换
      return Promise.resolve(res);
    })
    .catch(err => Promise.reject(err));
}

// V2 订单项更新请求接口
export interface V2OrderItemUpdateDto {
  productId: string;
  count: number;
  sourceId: string;
  sourceType?: number;
  lot?: string;
  thdLot?: string;
}

// V2 更新订单项
export const v2OrderItemUpdate = (id: string, itemData: V2OrderItemUpdateDto) => {
  console.log('=== V2 API: 更新订单项 ===');
  console.log('订单项ID:', id);
  console.log('请求数据:', itemData);
  console.log('请求URL:', v2Apis.orderItemUpdate(id));

  if (!id || (id && !isString(id) && isStringEmpty(id))) {
    console.error('订单项ID验证失败');
    return Promise.reject(10012);
  }

  if (!itemData.productId || !isNumber(itemData.count)) {
    console.error('产品ID或数量验证失败');
    return Promise.reject(10012);
  }

  console.log('参数验证通过，发送请求...');

  return request<V2InventoryOrder, V2OrderItemUpdateDto>(v2Apis.orderItemUpdate(id), {
    params: { body: itemData }
  })
    .then(({ body: res }) => {
      console.log('V2 API响应成功:', res);
      return Promise.resolve(res);
    })
    .catch(err => {
      console.error('V2 API响应失败:', err);
      return Promise.reject(err);
    });
}
